// packages/shared/src/wallet/types.ts
//
// Wallet domain TS interfaces + Zod schemas.
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
//
// Design invariants:
//   - balance is always integer cents, never < 0
//   - currency is ISO-4217 (default "USD" per F-01)
//   - Wallet states: active | frozen_pending_recovery | suspended
//   - Transaction history cursor is base64 opaque (server-generated)
import { z } from "zod";
// ObjectIdHex and CurrencyCode schemas re-used from ledger/types when available.
// Inline copies used here to avoid a hard ordering dependency during Wave 1
// parallel builds — reviewer deduplicates post-wave.
// TODO(Wave 1+1): import ObjectIdHexSchema, CurrencyCodeSchema from '../ledger/types.js'
/** MongoDB ObjectId hex string (24 lowercase hex chars). */
const ObjectIdHexSchema = z.string().regex(/^[a-f0-9]{24}$/, {
    message: "Must be a 24-character lowercase hex ObjectId",
});
/** ISO-4217 currency code, exactly 3 uppercase letters. Default: USD (F-01). */
const CurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, {
    message: "Must be a 3-letter ISO-4217 currency code (e.g. USD, EUR)",
});
// ---------------------------------------------------------------------------
// WalletStatus — lifecycle states
// ---------------------------------------------------------------------------
/**
 * Wallet lifecycle status.
 *
 * - active                  — normal operating state
 * - frozen_pending_recovery — chargeback shortfall; recovery worker active;
 *                             deposits allowed, withdrawals blocked
 * - suspended               — admin action; fully blocked (read-only)
 */
export const WalletStatusSchema = z.enum([
    "active",
    "frozen_pending_recovery",
    "suspended",
]);
// ---------------------------------------------------------------------------
// Wallet — per-merchant wallet snapshot
//
// Denormalized balance cache; the ledger (LedgerEntry) is source of truth.
// Invariant: balance >= 0 enforced absolutely.
// ---------------------------------------------------------------------------
export const WalletSchema = z.object({
    /** MongoDB ObjectId of the wallet document */
    _id: ObjectIdHexSchema,
    /** Owning merchant's ObjectId */
    merchantId: ObjectIdHexSchema,
    /**
     * Cached balance in integer cents (always >= 0).
     * Source-of-truth is the LedgerEntry rows; this is a derived, denormalized view.
     */
    balance: z.number().int().nonnegative(),
    /** ISO-4217 currency code; defaults to USD per F-01 /. */
    currency: CurrencyCodeSchema.default("USD"),
    status: WalletStatusSchema,
    /** Creation timestamp — unix milliseconds */
    createdAt: z.number().int().nonnegative(),
    /** Last update timestamp — unix milliseconds */
    updatedAt: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// TransactionHistoryFilter — cursor-paginated filter shape
//
// Sent as query params to GET /wallet/transactions (dual-mode pagination).
// ---------------------------------------------------------------------------
export const TransactionHistoryFilterSchema = z.object({
    /**
     * Multi-select filter by TxGroupType.
     * Empty array / omitted → all types.
     */
    type: z.array(z.string()).optional(),
    /**
     * Filter by payment gateway identifier (e.g. "stripe", "paypal").
     * Max 50 chars to prevent DoS via oversized query strings.
     */
    gateway: z.string().max(50).optional(),
    /** Start of date range — unix milliseconds, inclusive */
    from: z.number().int().optional(),
    /** End of date range — unix milliseconds, exclusive */
    to: z.number().int().optional(),
    /**
     * Opaque cursor string returned by previous page response.
     * Base64-encoded, server-generated. Do not construct manually.
     */
    cursor: z.string().optional(),
    /** Page size. Clamped 1–100; defaults to 20. */
    limit: z.number().int().min(1).max(100).default(20),
});
//# sourceMappingURL=types.js.map